import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import ComPDFKitViewer from "@compdfkit_pdf_sdk/webviewer";
import "./index.css";
import { Card, Flex, Button } from "antd";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { getFileUrl } from "../../config";
import UnsupportedPreview, { DownloadFileButton } from "../unsupported-preview";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "tiff"];
const COMPDF_EXTENSION = ["pdf"];

export default function COMPDFViewer({
  title = "Supporting document",
  attachmentFile,
  replaceFile,
  attachmentKey,
}) {
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [viewer, setViewer] = useState(null);

  // Memoize extension and file type checks
  const extension = useMemo(() => {
    const name = attachmentFile?.name ?? attachmentKey ?? "";
    return name.split(".").pop().toLowerCase();
  }, [attachmentFile, attachmentKey]);
  const isPDF = useMemo(
    () => COMPDF_EXTENSION.includes(extension),
    [extension]
  );
  const isImage = useMemo(
    () => IMAGE_EXTENSIONS.includes(extension),
    [extension]
  );

  // Memoized src for image and PDF
  const fileSrc = useMemo(() => {
    if (attachmentFile) {
      return URL.createObjectURL(attachmentFile);
    }
    return getFileUrl(attachmentKey);
  }, [attachmentFile, attachmentKey]);

  // Clean up object URL for images
  useEffect(() => {
    return () => {
      if (attachmentFile) {
        URL.revokeObjectURL(fileSrc);
      }
    };
  }, [attachmentFile, fileSrc]);

  // Initialize SDK and load document for PDFs
  useEffect(() => {
    let isMounted = true;
    if (!isPDF) return;

    let localViewer = null;
    ComPDFKitViewer.init(
      { path: "/", license: "Njg0YmEwYjM5OTZkNw==" },
      containerRef.current
    ).then((inst) => {
      if (!isMounted) return;
      setViewer(inst);
      inst.UI.textPopup.update([]);
      inst.UI.disableElements(["header"]);

      const src = attachmentFile
        ? attachmentFile
        : encodeURI(getFileUrl(attachmentKey));
      inst.UI.loadDocument(src, {
        filename: attachmentFile?.name,
        withCredentials: true,
      }).then(() => {
        try {
          const pageW = inst?.docViewer?.getPageWidth(0);
          const viewW = containerRef.current?.clientWidth || 0;
          if (pageW && viewW) {
            inst.docViewer.webViewerScaleChanged(viewW / pageW);
          }
        } catch (error) {
          console.error(error);
        }
      });
      localViewer = inst;
    });

    return () => {
      isMounted = false;
      if (localViewer && localViewer.destroy) {
        localViewer.destroy();
      }
    };
    // eslint-disable-next-line
  }, [isPDF, attachmentFile, attachmentKey]);

  // Handlers
  const askReplace = useCallback(() => fileInputRef.current?.click(), []);
  const onFileSel = useCallback(
    (e) => replaceFile?.(e.target.files[0]),
    [replaceFile]
  );

  return (
    <Card
      styles={{
        body: {
          maxHeight: "calc(100vh - 180px)",
          overflow: "hidden",
        },
      }}
      title={
        <Flex align="center" justify="space-between" gap="small">
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 16,
              marginTop: 4,
            }}
          >
            {title}
          </div>

          {isPDF && viewer && (
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                icon={<ZoomInOutlined />}
                onClick={() => viewer.docViewer.zoomIn()}
              />
              <Button
                icon={<ZoomOutOutlined />}
                onClick={() => viewer.docViewer.zoomOut()}
              />
            </SpaceBetween>
          )}

          <SpaceBetween direction="horizontal" size="xs">
            <DownloadFileButton fileKey={attachmentKey} />
            <Button type="primary" onClick={askReplace}>
              Replace
            </Button>
          </SpaceBetween>
        </Flex>
      }
    >
      {isPDF && (
        <div
          id="compdf"
          ref={containerRef}
          style={{
            height: "calc(100vh - 200px)",
            overflow: "hidden",
            display: viewer ? "" : "none",
          }}
        />
      )}

      {isImage && (
        <img
          src={fileSrc}
          alt="Preview"
          style={{
            objectFit: "contain",
            width: "100%",
            overflow: "hidden",
            height: "calc(100vh - 200px)",
          }}
        />
      )}

      {!isPDF && !isImage && <UnsupportedPreview fileKey={attachmentKey} />}

      {/* hidden input for Replace action */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="application/pdf,image/*"
        onChange={onFileSel}
      />
    </Card>
  );
}
